import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Quote, Sparkles, Wifi, Car, Coffee, Snowflake, Sun, Mountain, Waves, Trees,
  Dog, Cat, Cigarette, ChefHat, Bath, Bed, Tv, Music, Heart, Home, Key, Users
} from "lucide-react";

/**
 * PropertyDescription — visual rich description with magazine drop-cap,
 * animated quote marks, auto-extracted highlight chips with icons, and
 * "read more" expansion for long texts.
 *
 * Props:
 *  - text: string (description)
 *  - theme
 *  - title (optional)
 */

// Keyword → icon map (matches both Italian & English, fuzzy)
const HIGHLIGHT_RULES = [
  { match: /\b(wi-?fi|internet|fibra)\b/i, icon: Wifi, label: "Wi-Fi" },
  { match: /\b(parcheggio|posto auto|garage|parking)\b/i, icon: Car, label: "Parcheggio" },
  { match: /\b(colazione|breakfast|caff[èe])\b/i, icon: Coffee, label: "Colazione" },
  { match: /\b(aria condizionata|climatizz|a\/c|air condition)\b/i, icon: Snowflake, label: "A/C" },
  { match: /\b(terrazz[ao]|balcone|patio|veranda|outdoor)\b/i, icon: Sun, label: "Outdoor" },
  { match: /\b(vista|panoram|view)\b/i, icon: Mountain, label: "Vista" },
  { match: /\b(mare|spiaggia|beach|sea|costa)\b/i, icon: Waves, label: "Vicino al mare" },
  { match: /\b(giardino|garden|verde|park)\b/i, icon: Trees, label: "Giardino" },
  { match: /\b(cane|pet|dog|animal)\b/i, icon: Dog, label: "Pet-friendly" },
  { match: /\b(gatto|cat)\b/i, icon: Cat, label: "Gatti ammessi" },
  { match: /\b(fumat|smok|sigarett)\b/i, icon: Cigarette, label: "Fumatori" },
  { match: /\b(cucina|kitchen|cuoc[oa]|cucinare)\b/i, icon: ChefHat, label: "Cucina" },
  { match: /\b(piscina|pool|jacuzzi|vasca)\b/i, icon: Bath, label: "Piscina" },
  { match: /\b(letto matrimon|king|queen|king-?size|bed)\b/i, icon: Bed, label: "Letto matrimoniale" },
  { match: /\b(smart tv|netflix|tv|televisore)\b/i, icon: Tv, label: "Smart TV" },
  { match: /\b(silen|tranquill|quiet|relax)\b/i, icon: Music, label: "Zona tranquilla" },
  { match: /\b(coppi[ae]|romantic|honeymoon|love)\b/i, icon: Heart, label: "Romantico" },
  { match: /\b(famigli[ae]|bambin[oi]|family|kids|child)\b/i, icon: Users, label: "Famiglie" },
  { match: /\b(self check-?in|smart lock|cassetta|keybox)\b/i, icon: Key, label: "Self check-in" },
];

const MAX_HIGHLIGHTS = 6;

const PropertyDescription = ({ text = "", theme, title = "La casa" }) => {
  const [expanded, setExpanded] = useState(false);
  // Show ~10 lines by default (before the "Read more" button appears). At
  // ~85 chars/line on mobile that's ~850 chars; we cut on the closest word
  // boundary so we never end mid-word.
  const PREVIEW_CHARS = 850;
  const isLong = text.length > PREVIEW_CHARS + 40;
  let sliced = text.slice(0, PREVIEW_CHARS);
  if (isLong) {
    const lastSpace = sliced.lastIndexOf(" ");
    if (lastSpace > PREVIEW_CHARS - 120) sliced = sliced.slice(0, lastSpace);
  }
  const display = expanded || !isLong ? text : sliced.trim() + "…";

  if (!text) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="relative rounded-3xl overflow-hidden p-6 md:p-10"
      style={{
        background: "linear-gradient(135deg, #FAFAFB 0%, #F5F5F7 100%)",
        border: `1px solid ${theme.border}`
      }}
      data-testid="property-description-box"
    >
      {/* Animated background blobs */}
      <motion.div
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
        animate={{ scale: [1, 1.15, 1], rotate: [0, 18, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        style={{ background: "radial-gradient(circle, rgba(255,56,92,0.06), transparent 70%)" }}
      />
      <motion.div
        className="absolute -bottom-16 -left-16 w-52 h-52 rounded-full pointer-events-none"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
        style={{ background: "radial-gradient(circle, rgba(59,130,246,0.05), transparent 70%)" }}
      />

      {/* Header label with sparkles */}
      <div className="relative flex items-center gap-2 mb-3 md:mb-4">
        <motion.div
          animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Sparkles className="w-4 h-4" style={{ color: "#FF385C" }} />
        </motion.div>
        <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.sub }}>
          {title}
        </p>
      </div>

      {/* Big quote mark decoration */}
      <Quote
        className="absolute top-6 right-6 md:top-10 md:right-10 w-12 h-12 md:w-16 md:h-16 opacity-[0.07] pointer-events-none"
        style={{ color: theme.text }}
        strokeWidth={1}
      />

      {/* Body — testo semplice (capolettera rimosso) */}
      <div className="relative">
        <p
          className="leading-relaxed text-[15px] md:text-[17px] max-w-3xl"
          style={{ color: theme.text, fontWeight: 400, letterSpacing: "-0.005em" }}
          data-testid="description-body"
        >
          {display}
        </p>

        {isLong && (
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 md:mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all shadow-sm hover:shadow-md"
            style={{
              background: "linear-gradient(135deg, #10B981, #059669)",
              color: "#FFFFFF",
              boxShadow: "0 4px 14px -4px rgba(16,185,129,0.45)",
            }}
            data-testid="description-toggle"
          >
            <span>{expanded ? "Mostra meno" : "Leggi tutta la descrizione"}</span>
            <motion.span
              animate={{ rotate: expanded ? 180 : 0 }}
              className="inline-block"
            >
              ↓
            </motion.span>
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default PropertyDescription;
